import { capitalize } from "@mui/material";
import type { UseMutateAsyncFunction } from "@tanstack/react-query";
import { DASH_PLACEHOLDER } from "../../../constants/dashPlaceholder";
import { DaylightDateFormat } from "../../../constants/DaylightDateFormat";
import { type Prefix } from "../../../constants/filters/utils";
import { EMPTY_ROUTE_OBJECT } from "../../../constants/planning/RouteEmptyObject";
import { toastMessage } from "../../../constants/strings";
import { dashIfEmpty } from "../../../constants/utils";
import type { DeliveryRouteSequenceStatus } from "../../../hooks/react-query/useDeliveryRoutesSequenceStatus";
import { plannedRoutesQueryKey } from "../../../hooks/react-query/usePlannedRoutes";
import { SELECTED_DATE_PARAM_KEY } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { LINKED_FILTERS_PARAM_KEY } from "../../../hooks/useLinkedFilters";
import {
  PLANNING_SORT_BY_KEY,
  PLANNING_SORT_DIR_KEY,
} from "../../../hooks/usePlanningSortParams";
import { SEARCH_TERM_KEY } from "../../../hooks/useSearchTermParam";
import { UNPLANNED_SELECTED_DATE } from "../../../hooks/useUnplannedDatetimeSearchParam";
import { queryClient } from "../../../queryClientProvider";
import {
  Consignee,
  Shipment,
} from "../../../services/prePlanningService.types";
import type {
  OSADShipment,
  OSADShipments,
} from "../../../types/operations/osad.type";
import {
  PlanSummary,
  PlanTableFormat,
} from "../../../types/planning/plan.type";
import { shipmentsWithGeocodeSchema } from "../../../types/planning/shipmentSeq.type";
import { OldRoute } from "../../../types/route.type";
import type { ServiceCenter } from "../../../types/serviceCenter.type";
import { dateToFormat } from "../../../utils/dateTimeHelper";
import { keepSpecificParams } from "../../../utils/searchParamHelper";
import { toast } from "../../../utils/snackbarHelper";
import { sortShipmentsByStopSequence } from "../../../utils/sortComparator";
import { unifyShipmentContext } from "../../../utils/unifyShipmentContext";
import { SummaryContext } from "../shared/ActionsMenuContext";
import { formatTrailerTitle } from "../shared/utils";
import type { ShipmentWithAssignment } from "./search/ShipmentTable";
interface FormattedPlannedRoutes {
  sicId: number;
  date: number;
  plannedRoutes: PlanSummary[];
  shipments: Shipment[] | ShipmentWithAssignment[];
  includeUnassigned?: boolean;
  searchTerm?: string;
  timeZone?: string;
}

export const getFormattedPlannedRoutes = ({
  sicId,
  date,
  plannedRoutes,
  shipments,
  searchTerm = "",
  includeUnassigned = false,
  timeZone,
}: FormattedPlannedRoutes) => {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();
  const plans = plannedRoutes.map((plan) => {
    const planShipments = shipments
      .filter((shipment) => {
        const isPartOfPlan = plan.shipmentIds.includes(shipment.id);
        const isUnassigned =
          includeUnassigned &&
          (shipment.assignment === "none" ||
            shipment.assignment === "Unassigned");

        return isPartOfPlan || isUnassigned;
      })
      .filter((shipment) =>
        filterShipmentBySearchTerm(shipment, lowerCaseSearchTerm)
      );

    const { title, ...planWithoutTitle } = plan;

    const name = formatTrailerTitle(plan.name, title);

    const planTableFormat: PlanTableFormat = {
      ...planWithoutTitle,
      name,
      bills: plan.shipmentsCount,
      date,
      pallets: plan.shipmentsPallets,
      pieces: plan.shipmentsPieces,
      handlingUnits: plan.shipmentsHandlingUnits,
      route: null,
      routeEnd: plan.endPoint,
      routeName: plan.name,
      serviceCenterCode: plan.sic,
      shipments: planShipments.map((shipment) => ({
        ...shipment,
        shipmentId: shipment.id,
        stopSeq: shipment.stopSeq || 0,
      })),
      sicId,
      tags: plan.shipmentsTags,
      weight: plan.shipmentsWeight,
      weightUnit: plan.shipmentsWeightUnit,
      zones: plan.zone ? plan.zone.split(",") : [DASH_PLACEHOLDER],
    };
    return planTableFormat;
  });
  return filterPlanTableFormatBySearchTerm(
    plans,
    timeZone,
    lowerCaseSearchTerm
  );
};

interface FormatOsadToPlannedRoute {
  osad: OSADShipments;
  serviceCenter: ServiceCenter;
  date: number;
}

export const formatOsadToPlannedRoute = ({
  osad,
  date,
  serviceCenter,
}: FormatOsadToPlannedRoute): PlanTableFormat => ({
  ...osad.summary,
  id: -1,
  type: "osad",
  name: "OSAD",
  date,
  door: 0,
  sicId: serviceCenter.id,
  serviceCenterCode: serviceCenter.code,
  startTime: null,
  endTime: null,
  routeEnd: null,
  isCommitted: null,
  routeName: "OSAD",
  presetRouteId: 0,
  location: "",
  zones: [],
  driver: null,
  tractor: null,
  trailer: null,
  straightTruck: null,
  stopOffCount: 0,
  weightUnit: "lbs",
  tags: osad.summary.billsByTags.map((t) => ({ ...t, description: "" })),
  shipmentIds: osad.shipments.map((shipment) => shipment.shipmentId),
  shipments: [],
  route: null,
  status: "",
  isManuallySequenced: false,
  isPlanCompleted: false,
  isAvailableForMassage: false,
  canEditStartTime: false,
  isBlockedFromTransition: false,
  hasSearchTerm: false,
});

const getRouteByRouteId = (routeId: number, allRoutes: OldRoute[]): OldRoute =>
  allRoutes.find((route) => route.id === routeId) ?? EMPTY_ROUTE_OBJECT;

export const getFormattedPlannedRoutesV2 = (
  sicId: number,
  date: number,
  plannedRoutes: PlanSummary[],
  routes: OldRoute[],
  shipments: Shipment[],
  searchTerm = "",
  lockedRoutes?: number[]
): PlanTableFormat[] =>
  plannedRoutes
    .map((plan) => {
      const shipmentsMap = new Map();

      // TODO: Get stop sequence and geocode for summary shipments
      const plannedShipments = shipments.filter(
        (shipment) =>
          plan.shipmentIds.includes(shipment.id) &&
          (!shipment.routeId || shipment.routeId === plan.id)
      );

      plannedShipments.forEach((shipment) => {
        const previous = shipmentsMap.get(shipment.id);
        shipmentsMap.set(shipment.id, { ...previous, ...shipment });
      });

      const formattedShipments =
        searchTerm && !plan.hasSearchTerm
          ? [...shipmentsMap.values()].filter((shipment) =>
              filterShipmentBySearchTerm(shipment, searchTerm)
            )
          : [...shipmentsMap.values()];

      const { title, ...planWithoutTitle } = plan;

      const name = formatTrailerTitle(plan.name, title);

      const planTableFormat: PlanTableFormat = {
        ...planWithoutTitle,
        name,
        bills: plan.shipmentsCount,
        date,
        isCommitted:
          plan.isCommitted ||
          (lockedRoutes ? lockedRoutes.includes(plan.id) : false),
        pallets: plan.shipmentsPallets,
        pieces: plan.shipmentsPieces,
        handlingUnits: plan.shipmentsHandlingUnits,
        route: getRouteByRouteId(plan.presetRouteId, routes),
        routeEnd: plan.endPoint,
        routeName: plan.name,
        serviceCenterCode: plan.sic,
        shipments: formattedShipments,
        sicId,
        tags: plan.shipmentsTags,
        weight: plan.shipmentsWeight,
        weightUnit: plan.shipmentsWeightUnit,
        zones: plan.zone ? plan.zone.split(",") : [DASH_PLACEHOLDER],
      };

      return planTableFormat;
    })
    .filter((planItem) =>
      searchTerm
        ? planItem.hasSearchTerm ||
          planItem.shipments.some((s) => s.highlightedRow)
        : true
    );

export const oldGetFormattedPlannedRoutes = (
  sicId: number,
  date: number,
  plannedRoutes: PlanSummary[],
  lockedRoutes: number[],
  routes: OldRoute[],
  shipments: Shipment[],
  searchTerm = ""
): PlanTableFormat[] =>
  plannedRoutes.map((plan) => {
    const shipmentsMap = new Map();

    // TODO: Get stop sequence and geocode for summary shipments
    const plannedShipments = shipments.filter(
      (shipment) =>
        plan.shipmentIds.includes(shipment.id) &&
        (!shipment.routeId || shipment.routeId === plan.id)
    );

    plannedShipments.forEach((shipment) => {
      const previous = shipmentsMap.get(shipment.id);
      shipmentsMap.set(shipment.id, { ...previous, ...shipment });
    });

    const formattedShipments = [...shipmentsMap.values()];

    const planTableFormat: PlanTableFormat = {
      ...plan,
      bills: plan.shipmentsCount,
      date,
      isCommitted: plan.isCommitted || lockedRoutes.includes(plan.id),
      pallets: plan.shipmentsPallets,
      pieces: plan.shipmentsPieces,
      handlingUnits: plan.shipmentsHandlingUnits,
      route: getRouteByRouteId(plan.presetRouteId, routes),
      routeEnd: plan.endPoint,
      routeName: plan.name,
      serviceCenterCode: plan.sic,
      shipments: searchTerm
        ? formattedShipments.filter(
            (s) =>
              s.highlightedRow ||
              (plan.name &&
                plan.name
                  .toString()
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()))
          )
        : formattedShipments,
      sicId,
      tags: plan.shipmentsTags,
      weight: plan.shipmentsWeight,
      weightUnit: plan.shipmentsWeightUnit,
      zones: plan.zone ? plan.zone.split(",") : [DASH_PLACEHOLDER],
    };

    return planTableFormat;
  });

export const getConsigneeAddress = (consignee: Consignee | null): string => {
  if (!consignee) return DASH_PLACEHOLDER;

  const address = dashIfEmpty(consignee.address);
  const city = dashIfEmpty(consignee.city);
  const state = dashIfEmpty(consignee.state);
  const zipCode = dashIfEmpty(consignee.zipCode);

  return `${address}, ${city}, ${state} ${zipCode}`;
};

export const filterShipmentsBySearchTerm = (
  shipment: Shipment,
  searchTerm = "",
  timeZone?: string
): boolean =>
  shipment.proNumber.toLowerCase().includes(searchTerm) ||
  dateToFormat(
    shipment.serviceDueDate,
    DaylightDateFormat.DATE,
    timeZone
  ).includes(searchTerm) ||
  shipment.assignment?.toLowerCase().includes(searchTerm) ||
  shipment.shipper?.name?.toLowerCase().includes(searchTerm) ||
  shipment.consignee?.name?.toLowerCase().includes(searchTerm) ||
  shipment.consignee?.address?.toLowerCase().includes(searchTerm) ||
  shipment.consignee?.city?.toLowerCase().includes(searchTerm) ||
  shipment.consignee?.zipCode?.toLowerCase().includes(searchTerm) ||
  shipment.destinationSicCode?.toLowerCase().includes(searchTerm) ||
  shipment.pallets.toString().toLowerCase().includes(searchTerm) ||
  shipment.pieces.toString().toLowerCase().includes(searchTerm) ||
  shipment.weight.toString().toLowerCase().includes(searchTerm) ||
  shipment.tags.some((tag) => tag.name.toLowerCase().includes(searchTerm)) ||
  shipment.status.toLowerCase().includes(searchTerm) ||
  shipment.tripNumber?.toString().toLowerCase().includes(searchTerm) ||
  dateToFormat(
    shipment.etaDate,
    DaylightDateFormat.DATE_TIME_12,
    timeZone
  ).includes(searchTerm) ||
  false;

export const filterOsadShipmentBySearchTerm = (
  shipment: OSADShipment,
  search = "",
  timeZone?: string
): boolean => {
  const searchTerm = search.toLowerCase();
  return (
    shipment.proNumber.toLowerCase().includes(searchTerm) ||
    dateToFormat(
      shipment.serviceDueDate,
      DaylightDateFormat.DATE,
      timeZone
    ).includes(searchTerm) ||
    shipment.loadTo?.toLowerCase().includes(searchTerm) ||
    shipment.reasonCode.toLowerCase().includes(searchTerm) ||
    shipment.shipper?.name?.toLowerCase().includes(searchTerm) ||
    shipment.consignee?.name?.toLowerCase().includes(searchTerm) ||
    shipment.consignee?.city?.toLowerCase().includes(searchTerm) ||
    shipment.consignee?.zipCode?.toLowerCase().includes(searchTerm) ||
    shipment.destinationSicCode?.toLowerCase().includes(searchTerm) ||
    shipment.pallets.toString().toLowerCase().includes(searchTerm) ||
    shipment.pieces.toString().toLowerCase().includes(searchTerm) ||
    shipment.weight.toString().toLowerCase().includes(searchTerm) ||
    shipment.handlingUnits.toString().toLowerCase().includes(searchTerm) ||
    shipment.tags.some((tag) => tag.name.toLowerCase().includes(searchTerm)) ||
    shipment.status.toLowerCase().includes(searchTerm) ||
    false
  );
};

export const filterPlanSummaryBySearchTerm = (
  planSummary: PlanSummary,
  timeZoneId?: string,
  searchTerm?: string
): PlanSummary => {
  if (!searchTerm) {
    return planSummary;
  }

  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  return {
    ...planSummary,
    hasSearchTerm:
      planSummary.name.toLowerCase().includes(lowerCaseSearchTerm) ||
      planSummary.zone.toLowerCase().includes(lowerCaseSearchTerm) ||
      (planSummary.type === "delivery_route" &&
        dateToFormat(
          planSummary.startTime,
          DaylightDateFormat.MILITARY_TIME,
          timeZoneId
        ).includes(lowerCaseSearchTerm)) ||
      planSummary.driver?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      planSummary.tractor?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      planSummary.trailer?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      planSummary.straightTruck?.name
        ?.toLowerCase()
        .includes(lowerCaseSearchTerm) ||
      planSummary.shipmentsCount.toString().includes(lowerCaseSearchTerm) ||
      planSummary.shipmentsPallets.toString().includes(lowerCaseSearchTerm) ||
      planSummary.shipmentsWeight.toString().includes(lowerCaseSearchTerm) ||
      planSummary.shipmentsTags.some((tag) =>
        tag.name.toLowerCase().includes(lowerCaseSearchTerm)
      ) ||
      false,
  };
};

export const filterPlanTableFormatBySearchTerm = (
  plans: PlanTableFormat[],
  timeZoneId?: string,
  searchTerm = "",
  includeShipments = true
): PlanTableFormat[] => {
  if (!searchTerm) {
    return plans;
  }

  const lowerCaseSearchTerm = searchTerm ? searchTerm.toLowerCase() : "";

  const filteredPlans = plans.filter(
    (plan) =>
      plan.name.toLowerCase().includes(lowerCaseSearchTerm) ||
      plan.zones.includes(lowerCaseSearchTerm) ||
      (plan.type === "delivery_route" &&
        dateToFormat(
          plan.startTime,
          DaylightDateFormat.MILITARY_TIME,
          timeZoneId
        ).includes(lowerCaseSearchTerm)) ||
      plan.driver?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      plan.tractor?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      plan.trailer?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      plan.straightTruck?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
      (includeShipments &&
        plan.shipments.some((shipment) =>
          filterShipmentsBySearchTerm(shipment, lowerCaseSearchTerm, timeZoneId)
        ))
  );

  return filteredPlans.map((plan) => {
    const shipments = plan.shipments.filter((shipment) =>
      filterShipmentsBySearchTerm(shipment, lowerCaseSearchTerm, timeZoneId)
    );

    return {
      ...plan,
      shipments,
      hasSearchTerm:
        shipments.length > 0 ||
        plan.name.toLowerCase().includes(lowerCaseSearchTerm),
    };
  });
};

export const filterShipmentBySearchTerm = (
  shipment: Shipment | ShipmentWithAssignment,
  searchTerm: string
): boolean => {
  if (!searchTerm) return true;
  return (
    shipment.proNumber.includes(searchTerm) ||
    shipment.assignment?.toLowerCase().includes(searchTerm) ||
    shipment.consignee?.name?.toLowerCase().includes(searchTerm) ||
    shipment.shipper?.name?.toLowerCase().includes(searchTerm) ||
    false
  );
};

interface AutoSequenceRoutesFinishedProps {
  planDate: number;
  sicId: number;
  sequenceResult: DeliveryRouteSequenceStatus[];
  retryMutation: UseMutateAsyncFunction<number[], Error, number[], unknown>;
  searchTerm?: string;
  clearSelection?: () => void;
}

export const onAutoSequenceRoutesFinished = ({
  planDate,
  sicId,
  sequenceResult,
  retryMutation,
  clearSelection = () => {},
}: AutoSequenceRoutesFinishedProps) => {
  queryClient.setQueryData<PlanSummary[]>(
    plannedRoutesQueryKey(planDate, sicId),
    (previousStateOfPlans) => {
      if (!previousStateOfPlans) return [];

      // Map over the previous state to create a new array
      return previousStateOfPlans.map((plan) => {
        // Find the matching sequence status
        const sequence = sequenceResult.find((seq) => seq.id === plan.id);

        // If there's a matching sequence status, update the plan accordingly
        if (sequence) {
          if (sequence.sequenceStatus === "error") {
            return {
              ...plan,
              error: {
                message: "Auto-Sequence Failed",
                onRetry: () => retryMutation([plan.id]),
                isRetryLoading: false,
              },
            };
          }
          if (sequence.sequenceStatus === "processed") {
            return {
              ...plan,
              sequenceStatus: "processed",

              isManuallySequenced: false,
            };
          }
        }
        // Return the plan as-is if no matching sequence status
        return plan;
      });
    }
  );
  if (sequenceResult.every((route) => route.sequenceStatus === "processed")) {
    toast(toastMessage.inbound.autoSequencing.success(sequenceResult.length), {
      variant: "success",
      preventDuplicate: true,
    });
  } else if (
    sequenceResult.every((route) => route.sequenceStatus === "error")
  ) {
    toast(toastMessage.inbound.autoSequencing.failed(sequenceResult.length), {
      variant: "error",
      preventDuplicate: true,
    });
  } else {
    const routesWithErrors = sequenceResult.filter(
      (promise) => promise.sequenceStatus === "error"
    );
    toast(
      toastMessage.inbound.autoSequencing.someFailed(
        sequenceResult.length - routesWithErrors.length,
        routesWithErrors.length
      ),
      {
        variant: "warning",
        preventDuplicate: true,
      }
    );
  }
  clearSelection();
};

/**
 * Generates a location object pointing to the "search" route,
 * including only specific query parameters relevant to the planning search.
 *
 * @param {URLSearchParams} searchParams - The current URL search parameters.
 * @returns {{ pathname: string, search: string }} A location object with filtered search params.
 */
export const pathToPlanningSearch = (searchParams: URLSearchParams) => ({
  pathname: "search",
  search: String(
    keepSpecificParams(searchParams, [
      SELECTED_DATE_PARAM_KEY,
      UNPLANNED_SELECTED_DATE,
      SEARCH_TERM_KEY,
      ...inboundFilterParams("planning"),
    ])
  ),
});

/**
 * Derives the path to the main table view by removing the last two segments
 * from a detailed pathname (e.g., shipment detail pages).
 *
 * @param {string} pathname - The full pathname from which to derive the base path.
 * @returns {string | undefined} The base pathname pointing to the main table, or undefined if the input is too short.
 */
export const pathToMainTable = (pathname: string) => {
  // Split the pathname into an array of segments, filtering out any empty strings
  let pathSegments = pathname.split("/").filter(Boolean);
  // Check if the path has more than one segment
  if (pathSegments.length > 1) {
    // Remove the last two segments from the array (usually 'shipments' and an ID)
    pathSegments = pathSegments.slice(0, pathSegments.length - 2);
    // Reconstruct the path by joining the segments with '/'
    // and add a leading '/' to form a valid path
    return "/" + pathSegments.join("/");
  }
};

export const isAllShipmentsGeocodeValid = (shipments: Shipment[]) => {
  const orderedShipments = [...shipments].sort(sortShipmentsByStopSequence);
  const validShipments = shipmentsWithGeocodeSchema.parse(
    orderedShipments.filter((point) => point.lat && point.lng && point.stopSeq)
  );
  return validShipments.length === shipments.length;
};

/**
 * Returns a list of inbound filter parameter keys, optionally prefixed.
 *
 * If a `prefix` is provided, it will be prepended to each parameter name with its first letter capitalized.
 * For example, with prefix `"planning"`, the result would include `"planningBills"`, `"planningDriverName"`, etc.
 *
 * If `prefix` is `undefined`, the function returns the original parameter names without any prefixing.
 * In other words, it acts as a simple passthrough for the base filter parameter names.
 *
 * @param {Prefix} [prefix] - Optional prefix to be applied to each parameter key.
 * @returns {string[]} An array of parameter keys, either raw or prefixed.
 */
export const inboundFilterParams = (prefix?: Prefix) =>
  [
    "bills",
    "driverName",
    "endTime",
    "location",
    "name",
    "pallets",
    "pieces",
    "routeEnd",
    "startTime",
    "status",
    "tags",
    "tractorName",
    "trailerName",
    "type",
    "weight",
    "zone",
  ].map((filterParam) =>
    prefix ? prefix + capitalize(filterParam) : filterParam
  );

interface SelectionToContextProps {
  plan?: PlanTableFormat;
  filteredPlans: PlanTableFormat[];
  selection: string[];
}

export const selectionToContext = ({
  plan,
  selection,
  filteredPlans,
}: SelectionToContextProps): SummaryContext[] => {
  const getType = (type?: string): "route" | "trap" | "osad" =>
    type === "delivery_route"
      ? "route"
      : type === "delivery_trap"
        ? "trap"
        : "osad";

  if (plan) {
    return [
      {
        id: String(plan.id),
        shipmentIds: plan.shipmentIds,
        type: getType(plan.type),
      },
    ];
  }

  const contexts = selection.map((id) => {
    const matchedPlan = filteredPlans.find((p) => String(p.id) === id);

    return {
      id,
      shipmentIds: matchedPlan?.shipmentIds ?? [],
      type: getType(matchedPlan?.type),
    };
  });

  return unifyShipmentContext(contexts);
};

export const formatShipmentWithAssignment = (
  plans: PlanTableFormat[],
  shipments: Shipment[]
) => {
  const shipmentMap = new Map<number, ShipmentWithAssignment>();

  for (const plan of plans) {
    for (const shipment of plan.shipments.filter((planShipment) =>
      shipments.includes(planShipment)
    )) {
      const assignedShipment: ShipmentWithAssignment = {
        ...shipment,
        assignmentId: plan.id,
        type:
          plan.type === "delivery_route" ? "delivery_route" : "delivery_trap",
        isCommitted: Boolean(plan.isCommitted),
      };

      shipmentMap.set(shipment.id, assignedShipment);
    }
  }
  return Array.from(shipmentMap.values());
};

export const selectionToShipmentContext = (
  selection: string[],
  shipments: ShipmentWithAssignment[]
) =>
  unifyShipmentContext(
    shipments
      .filter((shipment) => selection.includes(String(shipment.id)))
      .map((shipment) => ({
        id: String(shipment.assignmentId),
        shipmentIds: [shipment.id],
        type: shipment.type === "delivery_route" ? "route" : "trap",
      }))
  );

export function syncPlanningParams(
  params: URLSearchParams,
  linkFilters: boolean,
  prefix: string,
  linkedPrefix: string,
  paramsToKeep: string[] = []
): URLSearchParams {
  const newParams = new URLSearchParams();
  newParams.append(LINKED_FILTERS_PARAM_KEY, linkFilters ? "true" : "false");
  const entries = Array.from(params.entries());

  const appended = new Set<string>();

  function appendUnique(key: string, value: string) {
    const id = `${key}:::${value}`;
    if (!appended.has(id)) {
      newParams.append(key, value);
      appended.add(id);
    }
  }

  if (linkFilters) {
    const prefixedKeys = entries
      .filter(([key]) => key.startsWith(prefix))
      .map(([key]) => key);
    const linkedKeys = entries
      .filter(([key]) => key.startsWith(linkedPrefix))
      .map(([key]) => key);

    const hasPrefixed = prefixedKeys.length > 0;
    const hasLinked = linkedKeys.length > 0;

    if (!hasLinked && !hasPrefixed) {
      // keep only paramsToKeep
      for (const [key, value] of entries) {
        if (paramsToKeep.includes(key)) {
          appendUnique(key, value);
        }
      }
      return newParams;
    }

    if (!hasLinked && hasPrefixed) {
      for (const [key, value] of entries) {
        if (key.startsWith(prefix)) {
          const suffix = key.slice(prefix.length);
          const linkedKey = `${linkedPrefix}${suffix}`;
          appendUnique(key, value);
          appendUnique(linkedKey, value);
        } else if (paramsToKeep.includes(key)) {
          appendUnique(key, value);
        }
      }
      return newParams;
    }

    for (const [key, value] of entries) {
      const isPlanning = key.startsWith(prefix);
      const isLinked = key.startsWith(linkedPrefix);
      const isKept = paramsToKeep.includes(key);

      if (isPlanning) {
        const suffix = key.slice(prefix.length);
        const linkedKey = `${linkedPrefix}${suffix}`;
        appendUnique(key, value);
        appendUnique(linkedKey, value);
      } else if (isLinked || isKept) {
        appendUnique(key, value);
      }
    }
  } else {
    const linkedFiltersMap = new Map<string, string[]>();
    let hasAnyLinked = false;

    for (const [key, value] of entries) {
      if (key.startsWith(linkedPrefix)) {
        hasAnyLinked = true;
        const suffix = key.slice(linkedPrefix.length);
        if (!linkedFiltersMap.has(suffix)) linkedFiltersMap.set(suffix, []);
        const existingValues = linkedFiltersMap.get(suffix);
        if (existingValues) {
          existingValues.push(value);
        }
      }
    }

    for (const [key, value] of entries) {
      const isPlanning = key.startsWith(prefix);
      const isKept = paramsToKeep.includes(key);

      if (isKept) {
        appendUnique(key, value);
      } else if (isPlanning && hasAnyLinked) {
        appendUnique(key, value);
      }
    }

    if (hasAnyLinked) {
      for (const [suffix, values] of linkedFiltersMap.entries()) {
        const planningKey = `${prefix}${suffix}`;
        for (const val of values) {
          appendUnique(planningKey, val);
        }
      }
    }
  }

  return newParams;
}

export const inboundFilterParamsToKeep = (
  isFiltersLinked: boolean,
  includePlanDate = false,
  includeSortParams = false
) => {
  const filters = [
    LINKED_FILTERS_PARAM_KEY,
    ...inboundFilterParams(!isFiltersLinked ? "planning" : "linked"),
    ...inboundFilterParams("routes"),
    ...inboundFilterParams("traps"),
  ];

  const sortParams = includeSortParams
    ? [PLANNING_SORT_BY_KEY, PLANNING_SORT_DIR_KEY]
    : [];

  if (includePlanDate) {
    return [
      SELECTED_DATE_PARAM_KEY,
      UNPLANNED_SELECTED_DATE,
      ...filters,
      ...sortParams,
    ];
  }
  return [...filters, ...sortParams];
};
/**
 * Returns an object with afterRouteStop when feature flag is enabled and value is defined
 * @param isFeatureEnabled Feature flag boolean to check
 * @param afterRouteStop The afterRouteStop value to include
 */
export const getAfterRouteStopParam = (
  afterRouteStop?: number | null
): { afterRouteStop: number | null } | Record<string, never> =>
  afterRouteStop !== undefined
    ? { afterRouteStop }
    : ({} as Record<string, never>);

/**
 * Finds a single route that contains all the specified shipment IDs.
 * Returns the route if all shipments belong to exactly one route, null otherwise.
 *
 * @param plans - Array of plan table formats to search through
 * @param shipmentIds - Array of shipment IDs to find routes for
 * @returns The single route containing all shipments, or null if shipments span multiple routes or no route
 */
export const findSingleRouteForShipments = (
  plans: PlanTableFormat[],
  shipmentIds: number[]
): PlanTableFormat | null => {
  if (shipmentIds.length === 0) return null;

  const routesWithShipments = plans.filter((plan) =>
    plan.shipments.some((shipment) => shipmentIds.includes(shipment.id))
  );

  // If all shipments belong to exactly one route, return that route
  if (routesWithShipments.length === 1) {
    return routesWithShipments[0];
  }

  // If shipments belong to multiple routes or no route, return null
  return null;
};
